// src/components/results/PartTab.jsx
//
// One exam part (Lesen, Sprachbausteine, Hören) in depth: the part's score as an ink
// line, the answer sheet Teil by Teil (hover a square for the question, click to open
// it in place), the traps that caught the student, what held up, two next steps built
// from the taxonomy's practice links, and the door to "Review my mistakes" for this part.
// Every number on the page is counted from resultInsights; nothing is invented.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import QuestionPanel from './QuestionPanel';
import { familyMissPct } from './peerText';
import { causeGroup, familyEntry, fmt, inkPath, labelOf, pick, practiceLinks, questionText, itemKey } from './storyText';

const HAND = { fontFamily: "'Caveat', cursive" };
const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const short = (s, n = 110) => (s && s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s || '');

export default function PartTab({
  sectionKey, name, score, items, qIndex, idx, exam, passPct, formatAnswer, isFree, onPractice, onLocked, onReview, hasFlags, norms,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const wrongItems = useMemo(() => items.filter((it) => !it.correct), [items]);
  const pass = score.max ? score.score / score.max >= passPct / 100 : false;
  const gap = Math.abs(score.score - score.max * passPct / 100);

  // Teil by Teil, in exam order
  const teile = useMemo(() => {
    const out = [];
    items.forEach((it) => {
      let g = out[out.length - 1];
      if (!g || g.partId !== it.partId) {
        const { part } = qIndex[itemKey(it)];
        g = { partId: it.partId, part, items: [], got: 0, max: 0 };
        out.push(g);
      }
      g.items.push(it); g.max += it.value; if (it.correct) g.got += it.value;
    });
    return out;
  }, [items, qIndex]);

  // Starts on the first mistake worth looking at, like the corrected paper would.
  const reviewOrder = useMemo(() => [...wrongItems].sort((a, b) => (Number(b.confident) - Number(a.confident)) || (b.lost - a.lost)), [wrongItems]);
  const [sel, setSel] = useState({ id: reviewOrder[0] ? itemKey(reviewOrder[0]) : null, n: 0 });
  const selItem = sel.id ? items.find((it) => itemKey(it) === sel.id) : null;
  const pickSquare = (id) => setSel((s) => ({ id: s.id === id ? null : id, n: s.n + 1 }));
  const whereOf = (it) => `${name} · ${t('examResults.story.teil', { n: qIndex[itemKey(it)].part.partNumber })} · ${t('examResults.story.questionN', { n: it.number })}`;

  // What fooled you: wrong answers grouped by trap or language family
  const fooled = useMemo(() => {
    const groups = new Map();
    wrongItems.forEach((it) => {
      const g = causeGroup(it, idx, lang, t);
      if (!g?.label) return;
      const cur = groups.get(g.key) || { ...g, count: 0, lost: 0 };
      cur.count += 1; cur.lost += it.lost;
      groups.set(g.key, cur);
    });
    return [...groups.values()].sort((a, b) => (b.count - a.count) || (b.lost - a.lost)).slice(0, 3);
  }, [wrongItems, idx, lang, t]);

  // What held up: skills (or language families in Sprachbausteine), right out of all
  const held = useMemo(() => {
    const groups = new Map();
    items.forEach((it) => {
      const [kind, id] = sectionKey === 'sprachbausteine' && it.languageFamily ? ['language', it.languageFamily] : ['skill', it.skill];
      const label = labelOf(idx, kind, id, lang);
      if (!label) return;
      const cur = groups.get(label) || { label, kind, id, right: 0, all: 0 };
      cur.all += 1; if (it.correct) cur.right += 1;
      groups.set(label, cur);
    });
    return [...groups.values()].filter((g) => g.all >= 2).sort((a, b) => (b.all - a.all)).slice(0, 5);
  }, [items, sectionKey, idx, lang]);

  // The teacher's note for this part: the strongest point, then where the points went.
  const best = [...held].filter((g) => g.all >= 3 && g.right / g.all >= 0.8).sort((a, b) => (b.right / b.all) - (a.right / a.all) || b.all - a.all)[0];
  const praise = best ? (pick(familyEntry(idx, best.kind, best.id)?.praise, lang) || t('examResults.story.part.solid', { what: best.label })) : '';
  const note = [
    praise,
    fooled[0] && (fooled[0].count > 1
      ? t('examResults.story.part.noteRepeat', { what: fooled[0].label, count: fooled[0].count })
      : t('examResults.story.part.noteSingle', { count: wrongItems.length })),
  ].filter(Boolean).join(' ') || t(wrongItems.length ? 'examResults.story.part.noteSingle' : 'examResults.story.part.noteClean', { count: wrongItems.length });

  // Next steps: the skill that cost the most (strategy lesson), and the language point
  // that went wrong most often (rule page / exercises). Only links that exist.
  const aha = useMemo(() => {
    const cards = [];
    const bySkill = new Map(); const byPoint = new Map();
    wrongItems.forEach((it) => {
      if (it.skill) bySkill.set(it.skill.split('.')[0], (bySkill.get(it.skill.split('.')[0]) || 0) + it.lost);
      if (it.point) byPoint.set(it.point, (byPoint.get(it.point) || 0) + 1);
    });
    const [skillId, lost] = [...bySkill.entries()].sort((a, b) => b[1] - a[1])[0] || [];
    const skill = skillId ? familyEntry(idx, 'skill', skillId) : null;
    const strategy = skill ? practiceLinks(skill, exam.level, exam.examProvider, t, sectionKey).find((l) => l.state) : null;
    if (strategy) {
      cards.push({ key: 'skill', dark: true, kicker: t('examResults.story.aha.lost', { points: fmt(lost), what: pick(skill.label, lang) }), title: t('examResults.story.aha.strategyTitle', { what: pick(skill.label, lang) }), text: pick(skill.tip, lang), links: [strategy] });
    }
    const [pointId, count] = [...byPoint.entries()].sort((a, b) => b[1] - a[1])[0] || [];
    const point = pointId ? idx.point[pointId] : null;
    const pointLinks = point ? practiceLinks(point, exam.level, exam.examProvider, t, sectionKey) : [];
    if (pointLinks.length) {
      const all = items.filter((it) => it.point === pointId).length;
      cards.push({ key: 'point', dark: false, kicker: t('examResults.story.aha.pattern', { wrong: count, all }), title: pick(point.label, lang), text: [pick(point.tip, lang), point.example ? `„${point.example}“` : ''].filter(Boolean).join(' '), links: pointLinks });
    }
    return cards;
  }, [wrongItems, items, idx, exam, lang, t, sectionKey]);

  const sure = wrongItems.filter((it) => it.confident).length;
  const heroEnd = Math.round(Math.min(1, score.max ? score.score / score.max : 0) * 400);

  return (
    <div className="flex flex-col gap-9">
      {/* Part score */}
      <section className="rs-up rs-paper grid gap-x-9 gap-y-5 px-6 sm:px-7 pt-6 pb-7 items-center [grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr))]"
        style={{ borderTop: `6px solid ${pass ? '#1C1917' : '#DC2626'}`, backgroundPosition: '0 24px' }}>
        <div className="flex flex-col gap-2">
          <h2 className="m-0 text-[30px] sm:text-[34px] font-bold" style={DISPLAY}>{name}</h2>
          <div className="flex flex-wrap items-baseline gap-2.5">
            <span className="text-[54px] leading-none" style={HAND}>{fmt(score.score)} / {fmt(score.max)}</span>
            <span className={`font-bold ${pass ? 'text-[#006D77]' : 'text-red-700'}`}>
              {pass ? t('examResults.story.part.above', { n: fmt(gap), pct: passPct }) : t('examResults.story.part.toGo', { n: fmt(gap), pct: passPct })}
            </span>
          </div>
          <svg viewBox="0 0 420 34" className="w-full max-w-[440px] h-auto overflow-visible" aria-hidden="true">
            <line x1="0" y1="18" x2="400" y2="18" stroke="#E7E5E4" strokeWidth="2" />
            <path className="rs-stroke rs-draw" pathLength="1" stroke="#1C1917" strokeWidth="7" d={inkPath(heroEnd / 400, 400, 18)} style={{ animationDuration: '1.2s', animationDelay: '.3s' }} />
            <line x1={passPct * 4} y1="3" x2={passPct * 4} y2="33" stroke="#DC2626" strokeWidth="2.5" strokeDasharray="5 5" />
            {pass && <path className="rs-stroke rs-draw" pathLength="1" stroke="#DC2626" strokeWidth="3.2" d={`M${Math.min(heroEnd + 12, 396)} 16 l7 8 l14 -20`} style={{ animationDelay: '1.5s', animationDuration: '.35s' }} />}
          </svg>
        </div>
        <p className="m-0 text-[26px] sm:text-[30px] leading-[1.2] text-red-600" style={HAND}>
          <span className="redpen-note" style={{ animationDelay: '.9s' }}>{note}</span>
        </p>
      </section>

      {/* Teil by Teil */}
      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="m-0 text-[24px] sm:text-[28px] font-bold" style={DISPLAY}>{t('examResults.story.part.teileTitle')}</h2>
            <p className="m-0 mt-1.5 text-stone-600">{t('examResults.story.part.teileIntro')}</p>
          </div>
          <div className="flex flex-wrap gap-4 text-[13px] text-stone-700">
            <span className="inline-flex gap-1.5 items-center"><span className="w-3.5 h-3.5 rounded-[3px] bg-[#006D77]" />{t('examResults.story.legendRight')}</span>
            <span className="inline-flex gap-1.5 items-center"><span className="w-3.5 h-3.5 rounded-[3px] bg-white border-2 border-red-600" />{t('examResults.story.legendWrong')}</span>
            {hasFlags && <span className="inline-flex gap-1.5 items-center"><span className="w-3.5 h-3.5 rounded-[3px] bg-amber-200" />{t('examResults.story.legendFlagged')}</span>}
          </div>
        </div>
        <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr))]">
          {teile.map((g, gi) => {
            const mine = selItem && selItem.partId === g.partId;
            const what = t(`examResults.story.teilWhat.${g.part.taskType}`, { defaultValue: '' });
            return (
              <div key={g.partId} className={`bg-white rounded-lg px-[18px] pt-[18px] pb-5 flex flex-col gap-3 shadow-[0_18px_40px_-30px_rgba(28,25,23,.35)] ${mine ? 'outline outline-2 -outline-offset-2 outline-stone-900' : ''}`}>
                <div className="flex justify-between items-baseline gap-2">
                  <span className="font-bold text-lg" style={DISPLAY}>{t('examResults.story.teil', { n: g.part.partNumber })}</span>
                  <span className="text-[28px] leading-none whitespace-nowrap" style={{ ...HAND, color: g.max && g.got / g.max < passPct / 100 ? '#DC2626' : '#1C1917' }}>{fmt(g.got)} / {fmt(g.max)}</span>
                </div>
                {what && <span className="-mt-1.5 text-[13px] text-stone-600">{what}</span>}
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((it, ii) => {
                    const open = sel.id === itemKey(it);
                    const look = !it.correct
                      ? `${it.flagged ? 'bg-amber-200' : 'bg-white'} border-2 border-red-600 text-red-800`
                      : it.flagged ? 'bg-amber-200 border-2 border-amber-600 text-amber-900' : 'bg-[#006D77] border-2 border-[#006D77] text-white';
                    const tip = short(questionText(qIndex[itemKey(it)].q, qIndex[itemKey(it)].part) ?? t('examResults.story.whichHeading', { n: it.number }));
                    return (
                      <button key={itemKey(it)} type="button" onClick={() => pickSquare(itemKey(it))}
                        aria-expanded={open} aria-controls="rs-question-panel"
                        aria-label={t(it.correct ? 'examResults.story.squareRight' : 'examResults.story.squareWrong', { n: it.number })}
                        className={`rs-sq rs-focus relative w-11 h-11 rounded-md text-[13px] font-semibold touch-manipulation ${look} ${open ? 'ring-[3px] ring-stone-900' : ''}`}
                        style={{ animationDelay: `${(gi * 6 + ii) * 18}ms` }}>
                        {it.number}
                        {tip && <span className="rs-tip" role="tooltip">{tip}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {selItem && (
          <QuestionPanel
            key={`${itemKey(selItem)}-${sel.n}`}
            item={selItem} q={qIndex[itemKey(selItem)].q} part={qIndex[itemKey(selItem)].part}
            where={whereOf(selItem)} idx={idx} exam={exam} formatAnswer={formatAnswer}
            isFree={isFree} onPractice={onPractice} onLocked={onLocked}
            onClose={() => setSel((s) => ({ id: null, n: s.n + 1 }))}
            norms={norms}
            scroll={sel.n > 0}
          />
        )}
      </section>

      {/* What fooled you / what held up */}
      {(fooled.length > 0 || held.length > 0) && (
        <section className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(100%,360px),1fr))]">
          {fooled.length > 0 && (
            <div className="flex flex-col gap-3">
              <h2 className="m-0 text-2xl font-bold" style={DISPLAY}>{t('examResults.story.part.fooledTitle')}</h2>
              {fooled.map((g) => (
                <div key={g.key} className="bg-white rounded-md px-4 py-3.5 grid grid-cols-[54px_minmax(0,1fr)] gap-3 items-center shadow-[0_14px_30px_-26px_rgba(28,25,23,.4)]">
                  <span className="text-[34px] leading-none text-red-600 text-center" style={HAND}>{g.count}×</span>
                  <span className="flex flex-col gap-0.5">
                    <span className="font-bold">{g.label}</span>
                    {g.tip && <span className="text-sm text-stone-700 leading-relaxed">{g.tip}</span>}
                    {familyMissPct(norms, g.key) != null && (
                      <span className="text-[13px] font-semibold text-[#006D77]">{t('examResults.story.peer.family', { pct: familyMissPct(norms, g.key) })}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          {held.length > 0 && (
            <div className="flex flex-col gap-3">
              <h2 className="m-0 text-2xl font-bold" style={DISPLAY}>{t('examResults.story.part.heldTitle')}</h2>
              <div className="bg-white rounded-md px-[18px] py-4 flex flex-col gap-3 shadow-[0_14px_30px_-26px_rgba(28,25,23,.4)]">
                {held.map((g, i) => {
                  const r = g.right / g.all;
                  return (
                    <div key={g.label} className="grid grid-cols-[minmax(0,1fr)_minmax(90px,150px)_44px] gap-3 items-center">
                      <span className="text-sm font-semibold">{g.label}</span>
                      <svg viewBox="0 0 150 14" className="w-full h-auto overflow-visible" aria-hidden="true">
                        <line x1="0" y1="7" x2="150" y2="7" stroke="#E7E5E4" strokeWidth="2" />
                        <path className="rs-stroke rs-draw" pathLength="1" stroke={r >= 0.8 ? '#006D77' : r >= 0.5 ? '#1C1917' : '#DC2626'} strokeWidth="5"
                          d={`M2 7 Q ${Math.round(r * 75)} 5.5 ${Math.max(4, Math.round(r * 150))} 7`} style={{ animationDelay: `${0.2 + i * 0.15}s` }} />
                      </svg>
                      <span className="text-[22px] leading-none text-right" style={HAND}>{g.right}/{g.all}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Next steps */}
      {aha.length > 0 && (
        <section className="rs-up grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr))]">
          {aha.map((a) => (
            <div key={a.key} className={`rounded-[10px] px-[22px] pt-[22px] pb-6 flex flex-col gap-2.5 ${a.dark ? 'bg-stone-900 text-white' : 'bg-white border-2 border-stone-900'}`}>
              <span className={`text-[26px] leading-tight ${a.dark ? 'text-red-300' : 'text-red-600'}`} style={HAND}>{a.kicker}</span>
              <h3 className="m-0 text-xl leading-snug font-bold" style={DISPLAY}>{a.title}</h3>
              {a.text && <p className={`m-0 text-sm leading-relaxed ${a.dark ? 'text-stone-300' : 'text-stone-700'}`}>{a.text}</p>}
              <div className="flex flex-wrap gap-2.5 mt-1">
                {a.links.map((l) => {
                  const locked = l.premium && isFree;
                  const look = a.dark ? 'bg-white text-stone-900' : l.premium ? 'bg-stone-900 text-white' : 'border-2 border-stone-900 text-stone-900';
                  return (
                    <button key={l.label} type="button" onClick={() => (locked ? onLocked() : onPractice(l))}
                      className={`rs-focus min-h-[48px] px-4 rounded-[10px] font-bold inline-flex items-center gap-2 touch-manipulation active:scale-[0.98] transition-transform ${look}`}>
                      {locked && <Lock className="w-4 h-4" aria-hidden="true" />}
                      {l.label} · {l.premium ? t('examResults.story.premium') : t('examResults.story.free')}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Review my mistakes in this part */}
      <section>
        {wrongItems.length > 0 ? (
          <button type="button" onClick={() => onReview(sectionKey)}
            className="rs-lift rs-paper rs-focus group w-full text-left flex flex-wrap items-center justify-between gap-x-7 gap-y-[18px] px-6 sm:px-[26px] pt-[22px] pb-6 touch-manipulation"
            style={{ borderTop: '6px solid #DC2626', backgroundPosition: '0 20px' }}>
            <span className="flex-[999_1_380px] min-w-0 flex flex-col gap-2">
              <span className="text-[28px] leading-none text-red-600" style={HAND}>{t('examResults.story.part.reviewKicker', { count: wrongItems.length })}</span>
              <span className="text-[22px] sm:text-[26px] font-bold text-stone-900" style={DISPLAY}>{t('examResults.story.part.reviewTitle', { name })}</span>
              <span className="text-[15px] leading-relaxed text-stone-700">{hasFlags ? t('examResults.story.part.reviewText') : t('examResults.story.reviewIntroPaper')}</span>
              <span className="flex flex-wrap gap-2 mt-0.5">
                <span className="text-[13px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-800">{t('examResults.story.part.mistakes', { count: wrongItems.length })}</span>
                {sure > 0 && <span className="text-[13px] font-bold px-2.5 py-1 rounded-full bg-stone-900 text-white">{t('examResults.story.youWereSure')} · {sure}</span>}
              </span>
            </span>
            <span className="shrink-0 inline-flex items-center gap-2.5 min-h-[52px] px-[22px] rounded-xl bg-stone-900 text-white font-bold">
              {t('examResults.story.part.openReview')}
              <svg width="26" height="14" viewBox="0 0 26 14" className="overflow-visible transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true">
                <path d="M1 7 C 8 5, 15 8, 23 7 M18 2 L24 7 L18 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        ) : (
          <div className="rs-paper px-6 pt-5 pb-6" style={{ borderTop: '6px solid #006D77', backgroundPosition: '0 20px' }}>
            <span className="text-[28px] leading-none text-[#006D77]" style={HAND}>{t('examResults.story.part.nothingToFix', { name })}</span>
          </div>
        )}
      </section>
    </div>
  );
}
